import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import apiService from '../services/apiService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { cn } from '../lib/utils';
import {
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  Loader2,
  CheckCircle,
  Check,
  Circle
} from 'lucide-react';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [token, setToken] = useState('');

  useEffect(() => {
    const resetToken = searchParams.get('token');
    if (!resetToken) {
      setErrors({ token: 'Invalid or missing reset token. Please request a new password reset.' });
    } else {
      setToken(resetToken);
    }
  }, [searchParams]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.newPassword) {
      newErrors.newPassword = 'Password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters long';
    } else if (!/[a-z]/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain at least one lowercase letter';
    } else if (!/[A-Z]/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain at least one uppercase letter';
    } else if (!/\d/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain at least one number';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getPasswordStrength = (): { strength: number; label: string; color: string } => {
    const password = formData.newPassword;
    if (!password) return { strength: 0, label: '', color: '' };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z\d]/.test(password)) strength++;

    if (strength <= 2) return { strength, label: 'Weak', color: 'bg-destructive' };
    if (strength <= 4) return { strength, label: 'Medium', color: 'bg-yellow-500' };
    return { strength, label: 'Strong', color: 'bg-green-500' };
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      setErrors({ token: 'Invalid or missing reset token' });
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setErrors({});

      await apiService.post('/auth/reset-password', {
        token,
        newPassword: formData.newPassword
      });

      setSuccess(true);

      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || 'Failed to reset password';
      const errorCode = error.response?.data?.error?.code;

      if (errorCode === 'TOKEN_EXPIRED' || errorCode === 'INVALID_TOKEN') {
        setErrors({ token: errorMessage });
      } else if (error.response?.data?.error?.details) {
        setErrors(error.response.data.error.details);
      } else {
        setErrors({ newPassword: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength();

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8">
            {/* Success Icon */}
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-center mb-2 text-foreground">
              Password Reset Successful!
            </h1>

            {/* Message */}
            <p className="text-center text-muted-foreground mb-6">
              Your password has been successfully reset. You can now log in with your new password.
            </p>

            {/* Info Box */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800 text-center">
                Redirecting you to login page in 3 seconds...
              </p>
            </div>

            {/* Actions */}
            <Button asChild className="w-full">
              <Link to="/login">
                Go to Login
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8">
          {/* Logo/Icon */}
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Lock className="h-10 w-10 text-primary" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-center mb-2 text-foreground">
            Reset Your Password
          </h1>
          <p className="text-center text-muted-foreground mb-8">
            Please enter your new password below.
          </p>

          {/* Token Error */}
          {errors.token && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
              <p className="text-sm text-destructive">{errors.token}</p>
              <Link
                to="/forgot-password"
                className="text-sm text-destructive hover:underline font-medium mt-2 inline-block"
              >
                Request a new reset link
              </Link>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* New Password Field */}
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  className={cn("pr-10", errors.newPassword && "border-destructive")}
                  placeholder="Enter new password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>

              {/* Password Strength Indicator */}
              {formData.newPassword && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-muted-foreground">Password strength:</span>
                    <span className={cn(
                      "font-medium",
                      passwordStrength.label === 'Weak' && 'text-destructive',
                      passwordStrength.label === 'Medium' && 'text-yellow-600',
                      passwordStrength.label === 'Strong' && 'text-green-600'
                    )}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div
                      className={cn("h-full transition-all duration-300", passwordStrength.color)}
                      style={{ width: `${(passwordStrength.strength / 6) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {errors.newPassword && (
                <p className="text-sm text-destructive">{errors.newPassword}</p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={cn("pr-10", errors.confirmPassword && "border-destructive")}
                  placeholder="Confirm new password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Password Requirements */}
            <div className="bg-muted/50 border rounded-lg p-4">
              <h3 className="text-sm font-semibold text-foreground mb-2">Password must contain:</h3>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li className="flex items-center">
                  {formData.newPassword.length >= 8 ? (
                    <Check className="h-3 w-3 mr-2 text-green-600" />
                  ) : (
                    <Circle className="h-3 w-3 mr-2" />
                  )}
                  <span className={formData.newPassword.length >= 8 ? 'text-green-600' : ''}>
                    At least 8 characters
                  </span>
                </li>
                <li className="flex items-center">
                  {/[a-z]/.test(formData.newPassword) ? (
                    <Check className="h-3 w-3 mr-2 text-green-600" />
                  ) : (
                    <Circle className="h-3 w-3 mr-2" />
                  )}
                  <span className={/[a-z]/.test(formData.newPassword) ? 'text-green-600' : ''}>
                    One lowercase letter
                  </span>
                </li>
                <li className="flex items-center">
                  {/[A-Z]/.test(formData.newPassword) ? (
                    <Check className="h-3 w-3 mr-2 text-green-600" />
                  ) : (
                    <Circle className="h-3 w-3 mr-2" />
                  )}
                  <span className={/[A-Z]/.test(formData.newPassword) ? 'text-green-600' : ''}>
                    One uppercase letter
                  </span>
                </li>
                <li className="flex items-center">
                  {/\d/.test(formData.newPassword) ? (
                    <Check className="h-3 w-3 mr-2 text-green-600" />
                  ) : (
                    <Circle className="h-3 w-3 mr-2" />
                  )}
                  <span className={/\d/.test(formData.newPassword) ? 'text-green-600' : ''}>
                    One number
                  </span>
                </li>
              </ul>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              disabled={loading || !!errors.token}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting Password...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Button asChild variant="ghost" size="sm">
              <Link to="/login">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
